<template>
  <div>
    <b-container>
      <validation-observer v-slot="{ handleSubmit }">
        <b-form @submit.stop.prevent="handleSubmit(next)" autocomplete="off">
          <b-card>
            <template #header>
              <h6 class="mb-0">Detalle de padecimientos / tratamiento</h6>
            </template>

            <p v-if="hayAlgunSi" class="text-muted small mb-3">
              Para la/s persona/s que fueron afirmativas en la pregunta anterior, indique a continuación el detalle de
              la enfermedad y tratamiento que recibe.
            </p>

            <div v-if="hayAlgunSi" class="detalle-por-solicitante">
              <b-card
                v-for="(grupo, gIndex) in detallesPorSolicitante"
                :key="'sol-' + gIndex"
                class="mb-3 detalle-solicitante-card"
              >
                <template #header>
                  <h6 class="mb-0">{{ grupo.nombreSolicitante || "Solicitante" }}</h6>
                </template>
                <div
                  v-for="(item, index) in grupo.items"
                  :key="((item && item.pregunta) || '') + '-' + gIndex + '-' + index"
                  class="mb-3 persona-asegurar-card"
                >
                  <b-card no-body class="border persona-asegurar-inner">
                    <b-card-header
                      class="
                        d-flex flex-column flex-sm-row align-items-start align-items-sm-center
                        justify-content-between py-2 persona-asegurar-header bg-light
                      "
                    >
                      <div class="pr-sm-2 mb-2 mb-sm-0 flex-grow-1">                        
                        <span class="font-weight-medium d-block">{{ item.pregunta }}</span>
                      </div>
                      <b-button
                        pill
                        variant="outline-danger"
                        size="sm"
                        class="align-self-stretch align-self-sm-auto"
                        @click="borrarDatosItem(item)"
                      >
                        Borrar datos
                      </b-button>
                    </b-card-header>
                    <b-card-body class="pt-3 pb-3">
                      <b-row>
                        <b-col cols="12">                          
                          <TextComponent :form="item" name="tratamiento" 
                                         label="Padecimiento o Tratamiento" 
                                         type="text" rules="required|max:100"/>
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <TextComponent
                              type="date"
                              :form="item"                              
                              name="fechaInicio"                              
                              label="Fecha inicio"
                              rules="required|date_min:01/01/1900,DD/MM/YYYY|date_max:today,YYYY-MM-DD"
                          />
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <TextComponent
                            :key="'fechaFin-' + gIndex + '-' + index + '-' + ((item && item.fechaInicio) || '')"
                            type="date"
                            :form="item"
                            name="fechaFin"
                            label="Fecha de terminación"
                            :rules="`date_min:${formatDate(item && item.fechaInicio || '',
                                         'YYYY-MM-DD','DD/MM/YYYY')},DD/MM/YYYY`"
                          />
                        </b-col>
                        <b-col cols="12" md="12" lg="4">
                          <TextComponent 
                              :form="item" 
                              name="medicoHospital"
                              label="Médico / Hospital" 
                              type="text" 
                              rules="required|max:100"/>
                        </b-col>
                      </b-row>
                    </b-card-body>
                  </b-card>
                </div>
              </b-card>
            </div>

            <b-alert
              v-if="hayAlgunSi && !todosDetallesCompletos && form.padecimientos.length"
              show
              variant="warning"
              class="mt-2"
            >
              Complete al menos padecimiento/tratamiento para cada fila indicada.
            </b-alert>

            <b-row align-h="end" class="mt-4">
              <b-col md="4" cols="4" style="display: flex; justify-content: flex-end">
                <b-button @click="previous" variant="secondary">Anterior</b-button>
                <b-button type="submit" variant="primary">Siguiente</b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-form>
      </validation-observer>
    </b-container>
  </div>
</template>

<script>
import { PREGUNTAS_CUESTIONARIO_MEDICO } from "@/data/colombia/salud/Selects";
import TextComponent from "@/components/controls/TextComponent.vue";
import { formatDate } from "@/helpers/dateHelper";

function nombreCompletoPersona(persona) {
  return [persona.name, persona.lastName, persona.secondLastName]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
}

export default {
  name: "FormColombiaSalud04",
  components: { TextComponent },
  data() {
    return { form: {} };
  },
  computed: {
    hayAlgunSi() {
      if (!this.form.dependientes || !Array.isArray(this.form.dependientes)) return false;
      return this.form.dependientes.some((p) => (p.respuestas || []).some((r) => r === "S"));
    },
    detallesPorSolicitante() {
      const list = this.form.padecimientos || [];
      const byName = {};
      list.filter(Boolean).forEach((item) => {
        const nom = (item.nombreSolicitante || "").trim() || "Solicitante";
        if (!byName[nom]) byName[nom] = { nombreSolicitante: nom, items: [] };
        byName[nom].items.push(item);
      });
      return Object.values(byName);
    },
    todosDetallesCompletos() {
      if (!this.form.padecimientos || !this.form.padecimientos.length) return true;
      return this.form.padecimientos.every(
        (d) => (d.tratamiento || "").trim() !== "" && (d.nombreSolicitante || "").trim() !== ""
      );
    },
  },
  mounted() {
    this.$store.dispatch("app/loading", true);
    const form = this.$store.state.wizard.form;
    if (form === undefined || form === null || form === "") {
      this.$router.push("/form/colombia/salud/");
      this.$store.dispatch("app/loading", false);
      return;
    }
    this.form = form;
    if (!Array.isArray(this.form.padecimientos)) this.form.padecimientos = [];

    if (this.form.detallesPadecimientos && this.form.detallesPadecimientos.length) {
      this.form.padecimientos = this.form.detallesPadecimientos.map((e) => ({
        nombreSolicitante: e.nombreSolicitante || "",
        pregunta: e.labelPregunta || e.pregunta || "",
        tratamiento: e.padecimientoTratamiento || e.tratamiento || "",
        fechaInicio: e.fechaInicio || "",
        fechaFin: e.fechaFin || "",
        medicoHospital: e.medicoHospital || "",
      }));
      delete this.form.detallesPadecimientos;
    }

    const haySi = (this.form.dependientes || []).some((p) => (p.respuestas || []).some((r) => r === "S"));

    if (haySi) {
      const existentes = this.form.padecimientos;
      const list = [];
      (this.form.dependientes || []).forEach((persona) => {
        const resp = persona.respuestas || [];
        const nombreSolicitante = nombreCompletoPersona(persona) || persona.name || "";
        resp.forEach((r, idxPregunta) => {
          if (r === "S") {
            const meta = PREGUNTAS_CUESTIONARIO_MEDICO[idxPregunta];
            const labelText = meta ? meta.label : "";
            const previo = existentes.find(
              (e) => (e.nombreSolicitante || "") === nombreSolicitante && (e.pregunta || "") === labelText
            );
            list.push({
              nombreSolicitante,
              pregunta: labelText,
              tratamiento: previo ? previo.tratamiento || "" : "",
              fechaInicio: previo ? previo.fechaInicio || "" : "",
              fechaFin: previo ? previo.fechaFin || "" : "",
              medicoHospital: previo ? previo.medicoHospital || "" : "",
            });
          }
        });
      });
      this.form.padecimientos = list.length
        ? list
        : [
            {
              nombreSolicitante: "",
              pregunta: "",
              tratamiento: "",
              fechaInicio: "",
              fechaFin: "",
              medicoHospital: "",
            },
          ];
    } else {
      this.form.padecimientos = [];
    }

    this.form.padecimientos.forEach((item) => {
      if (!item.pregunta && item.numPregunta) {
        const idx = parseInt(item.numPregunta, 10) - 1;
        const p = PREGUNTAS_CUESTIONARIO_MEDICO[idx];
        if (p) item.pregunta = p.label;
      }
    });
    this.$store.dispatch("app/loading", false);
  },
  methods: {
    formatDate,
    previous() {
      this.$store
        .dispatch("wizard/save", { index: 3, form: this.form })
        .then(() => this.$router.push("/form/colombia-salud-3.html"));
    },
    borrarDatosItem(item) {
      if (!item) return;
      item.tratamiento = "";
      item.fechaInicio = "";
      item.fechaFin = "";
      item.medicoHospital = "";
    },
    next() {
      if (this.hayAlgunSi && !this.todosDetallesCompletos) {
        this.$bvToast.toast("Complete nombre del solicitante y padecimiento/tratamiento en cada fila.", {
          title: "Validación",
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.$store.dispatch("wizard/save", { index: 4, form: this.form }).then(() => {
        if (this.form.observations.includes("CO203")) {
          this.$router.push("/form/colombia-salud-6.html");
        } else {
          this.$router.push("/form/colombia-salud-5.html");
        }
      });
    }
  },
};
</script>

<style scoped>
.container {
  max-width: 100%;
}
.persona-asegurar-card .persona-asegurar-inner {
  border-radius: 0.35rem;
}
.persona-asegurar-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.detalle-solicitante-card .card-header {
  background-color: rgba(0, 39, 118, 0.08);
  font-weight: 600;
}
.detalle-solicitante-card .card-header h6 {
  color: var(--one);
}
</style>
